import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { getAssets, getBanks, getUserWallet, initiateSell, verifyAccount } from "../../api/apiServices";
import jsPDF from "jspdf";
import useAuthStatus from "../../hooks/useAuthStatus";

// Helper Functions
const getHoldingColor = (symbol) => {
  const colors = { 
    BTC: "#F7931A", ETH: "#627EEA", USDT: "#26A17B", 
    BNB: "#F3BA2F", SOL: "#9945FF", XRP: "#00AAE4" 
  };
  return colors[symbol] || "#64748B";
};

const getHoldingIcon = (symbol) => {
  const icons = { 
    BTC: "ri-bitcoin-line", ETH: "ri-eth-line", 
    USDT: "ri-money-dollar-circle-line", BNB: "ri-coin-line" 
  };
  return icons[symbol] || "ri-currency-line";
};

const parseCurrency = (str) => {
  if (!str) return 0;
  // Remove ₦, $, commas and %
  return parseFloat(str.replace(/[₦$,%]/g, ""));
};

export default function SellCrypto() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [step, setStep] = useState("select");
  const [walletData, setWalletData] = useState(null);
  const [liveRates, setLiveRates] = useState({});
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [cryptoInput, setCryptoInput] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [banks, setBanks] = useState([]);


  useEffect(() => {
    if (!isAuthenticated) return;
  
    const fetchAllData = async () => {
      try {
        // 1. Fetch User Wallet and Internal Assets in parallel
        const [walletRes, assetsData] = await Promise.all([
          getUserWallet(),
          getAssets()
        ]);
  
        // 2. Map internal assets to the rateMap
        const rateMap = {};
        assetsData.forEach(asset => {
          rateMap[asset.symbol] = {
            price: parseCurrency(asset.price),
            change: parseCurrency(asset.change),
            icon: asset.icon // We can use the live icon from the API too
          };
        });
        setLiveRates(rateMap);
  
        if (walletRes.status === true) {
          setWalletData(walletRes.data);
  
          // 3. Calculate Total Portfolio using internal rates
          let cryptoValue = 0;
          walletRes.data.wallets.forEach(wallet => {
            const rate = rateMap[wallet.currency]?.price || 0;
            cryptoValue += (wallet.balance || 0) * rate;
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAllData();
  }, [isAuthenticated]);


const realHoldings = useMemo(() => {
  if (!walletData?.wallets || !liveRates) return [];

  return walletData.wallets
    .filter(wallet => (wallet.balance || 0) > 0)
    .map(wallet => {
      const assetInfo = liveRates[wallet.currency];

      const rate = assetInfo?.price || 0;
      const valueNGN = (wallet.balance || 0) * rate;

      return {
        symbol: wallet.currency,
        amount: wallet.balance || 0,
        valueNGN: valueNGN,
        change24h: assetInfo?.change || 0,
        iconUrl: assetInfo?.icon || null, 
        color: getHoldingColor(wallet.currency),
        icon: getHoldingIcon(wallet.currency),
      };
    })
    .sort((a, b) => b.valueNGN - a.valueNGN);
}, [walletData, liveRates]);

  const selectedAssetRateObject = selectedHolding ? liveRates[selectedHolding.symbol] || 0 : 0;

  const selectedAssetRate = selectedAssetRateObject.price

  const nairaValue = cryptoInput && selectedAssetRate
    ? (parseFloat(cryptoInput) * selectedAssetRate * 0.985).toFixed(2)
    : "0.00";


  const fee = cryptoInput && selectedAssetRate
    ? (parseFloat(cryptoInput) * selectedAssetRate * 0.015).toFixed(2)
    : "0.00";
  
  useEffect(() => {
  const fetchBanks = async () => {
    const res = await getBanks();
    if (res.status) setBanks(res.data);
  };
  fetchBanks();
}, []);

const handleLookup = () => {
    if (accountNumber.length === 10) setAccountName("CHUKWUEMEKA OBI");
  };

//   const handleLookup = async () => {
//   if (accountNumber.length !== 10 || !bankCode) return;

//   try {
//     const res = await verifyAccount(
//       accountNumber,
//       bankCode
//     );

//     if (res.status) {
//       setAccountName(res.data.account_name);
//     }
//   } catch (err) {
//     alert("Invalid account details");
//   }
// };

  const handleConfirm = async () => {
    if (!selectedHolding || !cryptoInput || !bankName || !accountNumber || !accountName) return;

    setSubmitting(true);

    try {
      const payload = {
        assetSymbol: selectedHolding.symbol,
        cryptoAmount: parseFloat(cryptoInput),
        bankName,
        bankCode,
        accountNumber,
        accountName,
      };

      const response = await initiateSell(payload);

      if (response.status === true) {
        // Redirect to callback page with reference
        const sellRef = response.data.paystackReference || response.data.reference;
        navigate(`/sell-crypto?reference=${sellRef}`);
      } else {
        alert(response.message || "Failed to initiate sell");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Nivora - Sell Receipt", 105, 20, { align: "center" });

    doc.setFontSize(12);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 40);
    doc.text(`Sold: ${cryptoInput} ${selectedHolding.symbol}`, 20, 50);
    doc.text(`Received: ₦${parseFloat(nairaValue).toLocaleString()}`, 20, 60);
    doc.text(`Bank: ${bankName}`, 20, 70);
    doc.text(`Account: ${accountNumber}`, 20, 78);

    doc.setFont("helvetica", "normal");
    doc.text("Thank you for using Nivora!", 20, 100);

    doc.save(`Nivora_Sell_${selectedHolding.symbol}.pdf`);
  };

  const goBack = () => {
    if (step === "select") navigate("/home");
    else if (step === "amount") setStep("select");
    else if (step === "bank") setStep("amount");
    else if (step === "confirm") setStep("bank");
    else setStep("select");
  };

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl py-4 px-4 text-slate-800 text-sm placeholder-slate-300 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading market rates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button onClick={goBack} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
          <i className="ri-arrow-left-line text-slate-600 text-lg"></i>
        </button>
        <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Sell Crypto</h1>
      </div>

      <div className="px-5 py-4">
        <div className="flex gap-1.5 mb-6">
          {["Select", "Amount", "Bank", "Confirm"].map((s, i) => {
            const idx = step === "select" ? 0 : step === "amount" ? 1 : step === "bank" ? 2 : 3;
            return <div key={s} className="flex-1 h-1.5 rounded-full transition-all" style={{ background: i <= idx ? "#059669" : "#E2E8F0" }} />;
          })}
        </div>

        {step === "select" && (
          <div>
            <p className="text-slate-500 text-sm mb-4">Select a crypto asset to sell</p>
            {realHoldings.length > 0 ? (
              <div className="space-y-2">
                {realHoldings.map(h => (
                  <button key={h.symbol} onClick={() => { setSelectedHolding(h); setStep("amount"); }}
                    className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-4 flex items-center justify-between cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                    >
                      <img src={h.iconUrl} alt={h.symbol} className="w-7 h-7 object-contain" />
                    </div>
                      <div className="text-left">
                        <p className="text-slate-800 text-sm font-bold">{h.symbol}</p>
                        <p className="text-slate-400 text-xs">Balance: {h.amount} {h.symbol}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-800 text-sm font-semibold">₦{h.valueNGN.toLocaleString()}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-10 text-center">
                <i className="ri-wallet-3-line text-6xl text-slate-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Holdings Yet</h3>
                <p className="text-slate-500 mb-8">You don't have any crypto to sell.</p>
                <div className="flex flex-col gap-3">
                  <button onClick={() => navigate("/deposit")} className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-2">
                    <i className="ri-arrow-down-circle-line"></i> Deposit Crypto
                  </button>
                  <button onClick={() => navigate("/buy")} className="w-full py-4 border border-emerald-600 text-emerald-600 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-50">
                    <i className="ri-shopping-cart-line"></i> Buy Crypto
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === "amount" && selectedHolding && (
          <div>
            <div className="flex items-center gap-2 mb-5 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 w-fit">
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${selectedHolding.color}20` }}>
                <i className={`${selectedHolding.icon} text-xs`} style={{ color: selectedHolding.color }}></i>
              </div>
              <span className="text-slate-700 text-sm font-semibold">{selectedHolding.symbol}</span>
              <button onClick={() => setStep("select")} className="text-emerald-600 text-xs cursor-pointer ml-1 font-medium">Change</button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Sell ({selectedHolding.symbol})</p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={cryptoInput}
                  onChange={e => setCryptoInput(e.target.value)}
                  placeholder="0.00000000"
                  className="flex-1 bg-transparent text-slate-800 text-3xl font-bold placeholder-slate-200 focus:outline-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                />
                <button
                  onClick={() => setCryptoInput(String(selectedHolding.amount))}
                  className="text-emerald-600 text-xs cursor-pointer whitespace-nowrap font-semibold bg-emerald-50 px-2 py-1 rounded-lg"
                >
                  MAX
                </button>
              </div>
              <p className="text-slate-400 text-xs mt-1">Available: {selectedHolding.amount} {selectedHolding.symbol}</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Receive (NGN)</p>
              <div className="flex items-center gap-2">
                <span className="text-emerald-600 text-2xl font-bold">₦</span>
                <span className="text-slate-800 text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {parseFloat(nairaValue).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 space-y-2 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Rate</span>
                <span className="text-slate-600">1 {selectedHolding.symbol} = ₦{selectedAssetRate.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Fee (1.5%)</span>
                <span className="text-slate-600">₦{parseFloat(fee).toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={() => cryptoInput && parseFloat(cryptoInput) > 0 && parseFloat(cryptoInput) <= selectedHolding.amount ? setStep("bank") : undefined}
              disabled={!cryptoInput || parseFloat(cryptoInput) <= 0 || parseFloat(cryptoInput) > selectedHolding.amount}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700"
            >
              Continue
            </button>
          </div>
        )}

        {step === "bank" && (
          <div>
            <h2 className="text-slate-800 font-bold text-lg mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Bank Details</h2>
            <p className="text-slate-400 text-sm mb-6">Where should we send your Naira?</p>
            <div className="space-y-4">
              <div>
                <label className="text-slate-600 text-xs font-medium mb-2 block">Select Bank</label>
                <select 
                value={bankCode} 
                onChange={(e) => {
                  const selected = banks.find(b => b.code === e.target.value);
                  setBankCode(selected.code);
                  setBankName(selected.name);
                }}
                required className={inputCls + " appearance-none"}>
                  <option value="">-- Select your bank --</option>
                  {banks.map(bank => <option key={bank.code} value={bank.code}>{bank.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-slate-600 text-xs font-medium mb-2 block">Account Number</label>
                <div className="flex gap-2">
                  <input type="text" value={accountNumber} onChange={e => { setAccountNumber(e.target.value); setAccountName(""); }} maxLength={10} placeholder="10-digit account number"
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl py-4 px-4 text-slate-800 text-sm placeholder-slate-300 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors" />
                  <button onClick={handleLookup} disabled={accountNumber.length !== 10}
                    className="px-4 rounded-xl text-white text-sm font-semibold whitespace-nowrap disabled:opacity-40 cursor-pointer bg-emerald-600 hover:bg-emerald-700">Verify</button>
                </div>
                {accountName && <div className="flex items-center gap-2 mt-2"><i className="ri-check-line text-emerald-600 text-sm"></i><span className="text-emerald-600 text-xs font-semibold">{accountName}</span></div>}
              </div>
            </div>
            <button onClick={() => bankName && accountNumber && accountName ? setStep("confirm") : undefined}
              disabled={!bankName || !accountNumber || !accountName}
              className="w-full mt-8 py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700">Continue</button>
          </div>
        )}

        {step === "confirm" && (
          <div>
            <h2 className="text-slate-800 text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Confirm Sale</h2>
            <p className="text-slate-400 text-sm mb-6">Review before proceeding</p>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 mb-6">
              <div className="flex justify-between"><span className="text-slate-400 text-sm">You Sell</span><span className="text-slate-800 text-sm font-bold">{cryptoInput} {selectedHolding.symbol}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">You Receive</span><span className="text-emerald-600 text-sm font-bold">₦{parseFloat(nairaValue).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">Fee</span><span className="text-slate-600 text-sm">₦{parseFloat(fee).toLocaleString()}</span></div>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Bank</span><span className="text-slate-700 text-sm">{bankName}</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Account</span><span className="text-slate-700 text-sm">{accountNumber}</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Name</span><span className="text-emerald-600 text-sm font-semibold">{accountName}</span></div>
              </div>
            </div>
            <button 
              onClick={handleConfirm} 
              disabled={submitting}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? "Processing..." : "Confirm & Sell"}
            </button>
          </div>
        )}

        {step === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <i className="ri-loader-4-line text-emerald-600 text-4xl animate-spin"></i>
            </div>
            <h2 className="text-slate-800 text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Processing Sale</h2>
            <p className="text-slate-400 text-sm text-center">Converting {selectedHolding.symbol} and sending ₦{parseFloat(nairaValue).toLocaleString()} to your bank...</p>
          </div>
        )}

        {/* Success Step */}
        {step === "success" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-24 h-24 rounded-full bg-emerald-100 border-4 border-emerald-200 flex items-center justify-center mb-6">
              <i className="ri-check-line text-emerald-600 text-6xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-emerald-600 mb-2">Sale Initiated!</h2>
            <p className="text-slate-600 mb-8">Your request has been submitted. Naira will be sent to your bank shortly.</p>

            <button onClick={downloadReceipt} className="w-full max-w-xs py-4 border border-emerald-600 text-emerald-600 rounded-2xl font-semibold mb-3">
              Download Receipt
            </button>

            <button onClick={() => navigate("/home")} className="w-full max-w-xs py-4 bg-emerald-600 text-white rounded-2xl font-semibold">
              Back to Home
            </button>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
}
